import { useState, useCallback, useEffect } from "react";
import TableWrapper from "components/common/table-wrapper";
import {
  getTemplates,
  createTemplates,
  updateTemplates,
  deleteTemplates,
  deleteBulkTemplates,
} from "containers/templates/api";
import { DEBOUNCE_DELAY, initialMetaForTable } from "constants/common";
import { Pencil, Plus, Trash, TrashSimple } from "phosphor-react";
import { highlightFirstMatch } from "utils/common";
import Tooltip from "components/common/tooltip";
import CustomModal from "components/common/modal";
import TemplateForm from "./templates-form";

let timeout;

export const COLUMNS = [
  { columnKey: "name", label: "Name", isSortable: true },
  { columnKey: "question.description", label: "Question", isSortable: true },
];

const Templates = () => {
  const [templatesList, setTemplatesList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  const [selectedTemplates, setSelectedTemplates] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [isTemplateModelVisible, setIsTemplateModelVisible] = useState(false);
  const [meta, setMeta] = useState(initialMetaForTable);
  const [currentTemplateEdit, setCurrentTemplateEdit] = useState("");

  const handleOpenTemplateModel = () => setIsTemplateModelVisible(true);

  const handleCloseTemplateModel = () => {
    setIsTemplateModelVisible(false);
    setCurrentTemplateEdit("");
  };

  const debounceFn = (callback, delay) => {
    clearTimeout(timeout);
    timeout = setTimeout(callback, delay);
  };

  const handleSetSearchQuery = (value) => {
    setMeta((pre) => ({ ...pre, search: value }));
    debounceFn(handleRefreshPage, DEBOUNCE_DELAY);
  };

  const handleRefreshPage = () => {
    setRefreshPage((pre) => !pre);
  };

  const handlePageChange = (value) => {
    setMeta((pre) => ({ ...pre, page: value }));
    handleRefreshPage();
  };

  const handleSelectAll = () => {
    const updatedCheckboxes = templatesList.map((checkbox) => ({
      ...checkbox,
      checked: !selectAll,
    }));
    setTemplatesList(updatedCheckboxes);
    setSelectAll(!selectAll);
  };

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getTemplates({
        ...meta,
        key: sortConfig.key,
        order: sortConfig.direction,
      });
      if (result["templates"]) {
        const data = result?.templates.map((item) => ({
          ...item,
          checked: selectAll,
        }));
        setTemplatesList(data);
        setTotalCount(result?.meta.total_count);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  }, [refreshPage, sortConfig.direction, sortConfig.key]);

  const handleCheckboxChange = (index) => {
    setTemplatesList((pre) =>
      pre.map((item, itemIndex) =>
        itemIndex === index ? { ...item, checked: !item.checked } : item
      )
    );
  };

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    setSelectedTemplates(
      selectAll
        ? templatesList.length
        : templatesList.reduce((acc, item) => (item.checked ? acc + 1 : acc), 0)
    );
  }, [templatesList, selectAll]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const SortableTableHeader = ({
    columnKey,
    label,
    sortConfig,
    onSortChange,
  }) => {
    const isSorting = sortConfig.key === columnKey;
    const isAscending = isSorting && sortConfig.direction === "asc";
    const isDescending = isSorting && sortConfig.direction === "desc";

    const handleSort = () => {
      if (isAscending) {
        onSortChange(columnKey, "desc");
      } else {
        onSortChange(columnKey, "asc");
      }
    };

    return (
      <th onClick={handleSort} style={{ cursor: "pointer" }}>
        <div className="d-flex position-relative">
          <div className="d-flex flex-column cursor-pointer me-2 position-absolute top-50  end-100 translate-middle-y ">
            {/* <CaretUp size={12} color={isAscending ? '#000000' : '#00000050'} weight="fill" />
            <CaretDown color={isDescending ? '#000000' : '#00000050'} size={12} weight="fill" /> */}
          </div>
          <div>{label}</div>
        </div>
      </th>
    );
  };

  const handleTemplateSubmittion = async (
    template,
    setSubmitting,
    setErrors
  ) => {
    const { name, question_id } = template;
    const payload = { name, question_id };

    try {
      if (typeof currentTemplateEdit === "object") {
        const result = await updateTemplates(
          payload,
          currentTemplateEdit.id,
          setErrors
        );
        if (result) {
          handleCloseTemplateModel();
          handleRefreshPage();
        }
      } else {
        const result = await createTemplates(payload, setErrors);
        if (result) {
          handleCloseTemplateModel();
          handleRefreshPage();
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      const result = await deleteTemplates(id);
      if (result) {
        handleRefreshPage();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleBulkDeleteTemplates = async () => {
    const selectedIds = templatesList
      .filter((item) => item.checked)
      .map((item) => item.id);

    if (selectedIds.length === 0) return;

    try {
      const result = await deleteBulkTemplates(selectedIds);
      if (result) {
        handleRefreshPage();
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <TableWrapper
        searchPlaceholder="Search Templates"
        setSearhQuery={handleSetSearchQuery}
        searchValue={meta.search}
        totalListCount={totalCount}
        pageSize={meta.perPage}
        currentPage={meta.page}
        isLoading={loading}
        onPageChange={handlePageChange}
        selectedItems={selectedTemplates}
        upload={true}
        uploadKey={"Templates"}
        actionButtons={
          selectedTemplates > 0
            ? [
                {
                  label: `Delete ${selectedTemplates} Items`,
                  classes: "danger-btn m-0-imp",
                  icon: <TrashSimple size={24} className="me-3" />,
                  handleClick: handleBulkDeleteTemplates,
                },
              ]
            : [
                {
                  label: "Add Template",
                  classes: "primary-btn",
                  icon: <Plus size={24} className="me-3" />,
                  handleClick: () => handleOpenTemplateModel(),
                },
              ]
        }
      >
        <div className="container-fluid">
          <div className="table-responsive scrollable">
            <table className="table table-rows">
              <thead className="table-header">
                <tr>
                  <th>
                    <div className="form-check ps-3 mb-0">
                      <input
                        className="form-check-input light-grey-background"
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {COLUMNS.map((column) => (
                    <SortableTableHeader
                      key={column.columnKey}
                      columnKey={column.columnKey}
                      label={column.label}
                      isSortable={column.isSortable}
                      sortConfig={sortConfig}
                      onSortChange={handleSort}
                    />
                  ))}
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {templatesList?.map((template, index) => (
                  <tr key={template?.id || index}>
                    <td>
                      <div className="form-check ps-3 mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={template.checked}
                          onChange={(event) =>
                            handleCheckboxChange(index, event)
                          }
                          value={""}
                        />
                      </div>
                    </td>
                    <td>{template.name || "N/A"}</td>
                    <td className="fw-bold">
                      {highlightFirstMatch(
                        template?.question?.description || "N/A",
                        meta.search
                      )}
                    </td>
                    <td>
                      <Tooltip title={"Edit"}>
                        <Pencil
                          size={24}
                          className="opacity-50 me-5"
                          onClick={() => {
                            setCurrentTemplateEdit(template);
                            handleOpenTemplateModel();
                          }}
                          style={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                      <Tooltip title={"Delete"}>
                        <Trash
                          size={24}
                          className="opacity-50 ms-3"
                          onClick={() => handleDeleteTemplate(template?.id)}
                          style={{ cursor: "pointer" }}
                        />
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </TableWrapper>

      {isTemplateModelVisible && (
        <CustomModal
          size="sm"
          show={true}
          onHide={handleCloseTemplateModel}
          heading={`${currentTemplateEdit ? "Edit" : "Add"} Template`}
        >
          <TemplateForm
            handleTemplateSubmittion={handleTemplateSubmittion}
            handleClose={handleCloseTemplateModel}
            currentTemplateEdit={currentTemplateEdit}
          />
        </CustomModal>
      )}
    </>
  );
};

export default Templates;
